import posthog from 'posthog-js'
import { Component, ErrorInfo, ReactNode } from 'react'
import { Span } from '~/components/text'
import { logger } from '~/services/logger'

import { PageLevelError } from '../components'
import { ErrorBoundaryProps, ErrorBoundaryState } from '../types'
import { CrushedParamActions, CrushedParamError } from './CrushedParamErrorBoundary'

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Allow the app to be re-rendered when the page changes
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({ hasError: false, errorType: undefined })
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    if (error instanceof CrushedParamError) {
      // Update state so the next render will show the fallback UI.
      return { hasError: true, errorType: 'crush-error-param' }
    }

    return { hasError: true }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(`${error.name}: ${error.message}`, errorInfo)
    posthog.captureException(error, errorInfo)
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.errorType === 'crush-error-param') {
        return (
          <PageLevelError
            title="Ope!"
            description="We're unable to load this view because there is an invalid parameter."
            actions={<CrushedParamActions />}
          />
        )
      } else if (this.state.errorType === 'invalid-journey') {
        return <PageLevelError title="Ope!" description="Invalid journey." />
      } else {
        // You can render any custom fallback UI
        return (
          this.props.errorComponent ?? (
            <Span fontWeight="medium">Something went wrong. 😭</Span>
          )
        )
      }
    }

    return this.props.children
  }
}

export default ErrorBoundary
